"use client";

import React, { useEffect, useRef, useState } from "react";
import { getDaysInMonth, sub, add, isAfter, isBefore, isEqual, closestTo, set } from "date-fns";
import styles from "../Picker.module.scss";

type Month = {
  name: string;
  short: string;
};

type MonthsType = { [key: number]: Month };

const CONST_MONTHS: MonthsType = {
  0: { name: "January", short: "Jan" },
  1: { name: "February", short: "Feb" },
  2: { name: "March", short: "Mar" },
  3: { name: "April", short: "Apr" },
  4: { name: "May", short: "May" },
  5: { name: "June", short: "Jun" },
  6: { name: "July", short: "Jul" },
  7: { name: "August", short: "Aug" },
  8: { name: "September", short: "Sep" },
  9: { name: "October", short: "Oct" },
  10: { name: "November", short: "Nov" },
  11: { name: "December", short: "Dec" },
};

const VIEW_TYPE = {
  mobile: { padding: 80, height: 28 },
  desktop: { padding: 80, height: 35 },
};

interface DatePickerProps {
  initialDate?: Date;
  setDate?: (date: Date) => void;
  daysLimit?: number[];
  fullWidth?: boolean;
}

const today = set(new Date(), {
  hours: 0,
  minutes: 0,
  seconds: 0,
  milliseconds: 0,
});

const DatePicker: React.FC<DatePickerProps> = ({ initialDate = today, setDate, daysLimit, fullWidth = true }) => {
  // State to store selected month, day, and year
  const [selectedDate, setSelectedDate] = useState({
    day: initialDate?.getDate(),
    month: initialDate?.getMonth(),
    year: initialDate?.getFullYear(),
  });

  const limits = [sub(today, { days: daysLimit?.[0] || 0 }), add(today, { days: daysLimit?.[1] || 0 })];

  // Refs for each scrollable column
  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    scrollIntoSelectedDates();

    if (setDate && typeof setDate === "function") {
      const date = set(new Date(), {
        year: selectedDate.year,
        month: selectedDate.month, // 0-indexed month (10 = November)
        date: selectedDate.day,
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });
      setDate(date);
    }
  }, [selectedDate]);

  const validDays = (): number[] => {
    if (daysLimit && daysLimit.length) {
      const pastMonthYear =
        selectedDate.month === limits[0].getMonth() && selectedDate.year === limits[0].getFullYear();
      const futureMonthYear =
        selectedDate.month === limits[1].getMonth() && selectedDate.year === limits[1].getFullYear();

      if (pastMonthYear || futureMonthYear) {
        return Array.from(
          { length: getDaysInMonth(new Date(selectedDate?.year, selectedDate.month)) },
          (_, i) => i + 1
        ).filter((i) => {
          if (pastMonthYear) {
            return i >= limits[0].getDate();
          }

          return i <= limits[1].getDate();
        });
      }
    }

    return Array.from({ length: getDaysInMonth(new Date(selectedDate?.year, selectedDate.month)) }, (_, i) => i + 1);
  };

  const DAYS = validDays();

  const validMonths = (): MonthsType => {
    if (daysLimit && daysLimit.length) {
      const pastYear = selectedDate.year === limits[0].getFullYear();
      const futureYear = selectedDate.year === limits[1].getFullYear();

      if (pastYear || futureYear) {
        let start: number;
        let end: number;

        if (limits[0].getFullYear() === limits[1].getFullYear()) {
          start = limits[0].getMonth();
          end = limits[1].getMonth();
        } else {
          start = pastYear ? limits[0].getMonth() : 0;
          end = pastYear ? 11 : limits[1].getMonth();
        }

        const filter: MonthsType = {};

        Array.from({ length: end - start + 1 }).forEach((_, i: number) => {
          filter[start + i] = CONST_MONTHS[start + i];
        });

        return filter;
      }
    }

    return CONST_MONTHS;
  };

  const MONTHS: MonthsType = validMonths();

  const validYears = (): number[] => {
    let pastYear: number = 2000;
    let futureYear: number = 2099;

    if (daysLimit && daysLimit.length) {
      pastYear = limits[0].getFullYear();
      futureYear = limits[1].getFullYear();
    }

    return Array.from({ length: futureYear - pastYear + 1 }, (_, i: number) => pastYear + i);
  };

  const YEARS: number[] = validYears();

  const scrollIntoSelectedDates = (): void => {
    dayRef.current.scrollTop = VIEW_TYPE.mobile.height * (getIndex("day") + 1);
    monthRef.current.scrollTop = VIEW_TYPE.mobile.height * (getIndex("month") + 1);
    yearRef.current.scrollTop = VIEW_TYPE.mobile.height * (getIndex("year") + 1);
  };

  const getIndex = (type: "day" | "month" | "year"): number => {
    switch (type) {
      case "day":
        return DAYS.indexOf(selectedDate.day);
      case "month":
        return Object.keys(MONTHS).indexOf(selectedDate.month.toString());
      case "year":
        return YEARS.indexOf(selectedDate.year);
      default:
        return -1;
    }
  };

  const getCorrectItem = (index: number, array: any[]): number => {
    const length = array.length;

    if (index > length - 1) {
      return parseInt(array[length - 1], 10);
    }

    if (index < 0) {
      return parseInt(array[0], 10);
    }

    return parseInt(array[index], 10);
  };

  let scrollTimeout = null;

  // Calculate selected item based on scroll position
  const handleScroll = (ref: React.RefObject<HTMLElement>, type: "day" | "month" | "year"): void => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    scrollTimeout = setTimeout(() => {
      const scrollTop = ref.current.scrollTop - VIEW_TYPE.mobile.padding;
      const itemHeight = ref.current.clientHeight / 5;
      const index = Math.round((scrollTop + itemHeight * 2.5) / itemHeight);
      let selectedItem: number;

      switch (type) {
        case "day":
          selectedItem = getCorrectItem(index, DAYS);
          break;
        case "month":
          const months = Object.keys(MONTHS);
          selectedItem = getCorrectItem(index, months);
          break;
        case "year":
          selectedItem = getCorrectItem(index, YEARS);
          break;
      }

      setSelectedDate((prevDate) => {
        const newDate = {
          ...prevDate,
          [type]: selectedItem,
        };

        if (type === "year" && prevDate?.year !== selectedItem) {
          if (selectedItem > prevDate?.year) {
            // Future date, select first day of the year
            newDate["month"] = 0;
            newDate["day"] = 1;
          } else {
            // Past date, select last day of the year
            newDate["month"] = 11;
            newDate["day"] = 31;
          }
        }

        const date = new Date(newDate.year, newDate.month, newDate.day);

        if (
          !(daysLimit && daysLimit.length) ||
          ((isAfter(date, limits[0]) || isEqual(date, limits[0])) &&
            (isBefore(date, limits[1]) || isEqual(date, limits[1])))
        ) {
          return newDate;
        }

        const closestDate = closestTo(date, [limits[0], limits[1]]);

        return {
          day: closestDate.getDate(),
          month: closestDate.getMonth(),
          year: closestDate.getFullYear(),
        };
      });
    }, 50);
  };

  const handleClick = (index: number, type: "day" | "month" | "year"): void => {
    const scrollTo = VIEW_TYPE.mobile.height * (index + 1);

    switch (type) {
      case "day":
        dayRef.current.scrollTo({ top: scrollTo, behavior: "smooth" });
        break;
      case "month":
        monthRef.current.scrollTo({ top: scrollTo, behavior: "smooth" });
        break;
      case "year":
        yearRef.current.scrollTo({ top: scrollTo, behavior: "smooth" });
        break;
    }
  };

  // Scroll event listeners
  const onDayScroll = (): void => handleScroll(dayRef, "day");
  const onMonthScroll = (): void => handleScroll(monthRef, "month");
  const onYearScroll = (): void => handleScroll(yearRef, "year");

  return (
    <div className={`${styles.picker} ${styles.pickerDate} ${!fullWidth ? styles.fitContent : ""}`}>
      <div className={`${styles.column} ${styles.columnDate}`} ref={dayRef} onScroll={onDayScroll}>
        {DAYS.map((day, index) => (
          <div
            key={index}
            onClick={(): void => handleClick(index, "day")}
            className={`${styles.item} ${day === selectedDate.day ? styles.selected : ""}`}
          >
            {day}
          </div>
        ))}
      </div>
      <div className={`${styles.column} ${styles.columnMonth}`} ref={monthRef} onScroll={onMonthScroll}>
        {Object.entries(MONTHS).map(([key, month], index: number) => (
          <div
            key={index}
            onClick={(): void => handleClick(index, "month")}
            className={`${styles.item} ${parseInt(key) === selectedDate.month ? styles.selected : ""}`}
          >
            {month.short}
          </div>
        ))}
      </div>
      <div className={`${styles.column} ${styles.columnYear}`} ref={yearRef} onScroll={onYearScroll}>
        {YEARS.map((year: number, index: number) => (
          <div
            key={index}
            onClick={(): void => handleClick(index, "year")}
            className={`${styles.item} ${year === selectedDate.year ? styles.selected : ""}`}
          >
            {year}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DatePicker;
